<template>
  <div class="Availability-wrapper has-background-white mt-8 mb-8 pb-6 pr-6 pl-6 pt-6">
    <p class="hb-fonts-heading-5-bold mb-3">
      {{ availabilityTitle || 'Arzttermin ohne lange Wartezeit' }}
    </p>
    <p class="hb-fonts-body-regular has-text-weight-light is-italic">
      Wir haben für Sie in den nächsten 24 Stunden die folgenden Arzttermine frei:
    </p>
    <div>
      <AvailabilityTable
        :availability-per-slot="availabilityPerSlot"
        :first-day-date="firstDayDateString"
        :second-day-date="secondDayDateString"
      />
    </div>
    <p class="hb-fonts-body-regular has-text-weight-light is-italic">
      Sie können Ihren Termin ganz einfach direkt über die App buchen. Jetzt herunterladen und heute noch Termin vereinbaren.
    </p>
    <AppButtons
      :android-download-image="androidDownloadImage"
      :play-store-url="playStoreUrl"
      :ios-download-image="iosDownloadImage"
      :app-store-url="appStoreUrl"
      :tracking-screen="'App Landing CTA'"
      class="mt-5"
    />
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import AvailabilityTable from 'Patterns/vue/components/AvailabilityTable.vue'
import AppButtons from './AppButtons'

export default {
  name: 'AppointmentAvailability',

  components: {
    AvailabilityTable: AvailabilityTable,
    AppButtons: AppButtons
  },

  props: {
    availabilityEndpoint: {
      type: String,
      required: true
    },
    availabilityTitle: {
      type: String,
      required: false
    },
    androidDownloadImage: {
      type: String,
      required: false
    },
    playStoreUrl: {
      type: String,
      required: false
    },
    iosDownloadImage: {
      type: String,
      required: false
    },
    appStoreUrl: {
      type: String,
      required: false
    }
  },

  data () {
    return {
      timeSlotLength: 3,
      weekdaySlots: [7, 10, 13, 16, 19, 21],
      weekendSlots: [8, 11, 14, 17, 20],
      currentHour: moment().hour(),
      currentDay: moment().day(),
      firstTimeSlotIndex: 0,
      timeSlots: [],
      availabilityPerSlot: [],
      firstDayDateString: '',
      secondDayDateString: '',
      firstDayDateApi: '',
      secondDayDateApi: ''
    }
  },

  mounted () {
    moment.locale('de')
    this.init()
  },

  methods: {
    init () {
      const daysToDisplay = this.getDaysToDisplay()
      this.firstTimeSlotIndex = daysToDisplay[0].findIndex((time) => time > this.currentHour) - 1

      const firstDate = this.getFirstDate(this.firstTimeSlotIndex, daysToDisplay[0])
      const secondDate = this.getSecondDate(this.firstTimeSlotIndex)

      this.firstDayDateString = firstDate.format('DD. MMMM YYYY')
      this.secondDayDateString = secondDate ? secondDate.format('DD. MMMM YYYY') : ''

      this.firstDayDateApi = firstDate.format('YYYY-MM-DD')
      this.secondDayDateApi = secondDate ? secondDate.format('YYYY-MM-DD') : ''

      this.$set(this.timeSlots, 0, this.getTodayTimeSlot(...daysToDisplay))
      this.$set(this.timeSlots, 1, this.getTomorrowTimeSlot(...daysToDisplay))

      this.getAvailabilityPerDates(this.firstDayDateApi, this.secondDayDateApi)
    },

    getFirstDate (slotIndex, firstDaySlots) {
      const currentHour = moment().hour()
      const todayDate = moment()
      const tomorrowDate = moment().add(1, 'days')
      let firstDate = ''
      if (slotIndex <= 0) {
        firstDate = currentHour < firstDaySlots[1] ? todayDate : tomorrowDate
      } else {
        firstDate = todayDate
      }
      return firstDate
    },

    getSecondDate (slotIndex) {
      const tomorrowDate = moment().add(1, 'days')
      if (slotIndex > 0) {
        return tomorrowDate
      } else {
        return ''
      }
    },

    getDaysToDisplay () {
      let daysToDisplay = []

      switch (this.currentDay) {
        case 1: // Mon
        case 2: // Tue
        case 3: // Wed
        case 4: // Thu
          daysToDisplay = [this.weekdaySlots]
          break
        case 5: // Fri
          daysToDisplay = [this.weekdaySlots, this.weekendSlots]
          break
        case 6: // Sat
          daysToDisplay = [this.weekendSlots]
          break
        case 0: // Sun
          daysToDisplay = [this.weekendSlots, this.weekdaySlots]
          break
        default:
          daysToDisplay = [this.weekdaySlots]
      }

      return daysToDisplay
    },

    getTodayTimeSlot (firstDaySlots, secondDaySlots) {
      if (this.firstTimeSlotIndex <= 0) {
        return this.currentHour < firstDaySlots[1]
          ? firstDaySlots
          : secondDaySlots || firstDaySlots
      } else {
        return firstDaySlots.slice(this.firstTimeSlotIndex)
      }
    },

    getTomorrowTimeSlot (firstDaySlots, secondDaySlots) {
      if (this.firstTimeSlotIndex > 0) {
        return secondDaySlots
          ? secondDaySlots.slice(0, this.firstTimeSlotIndex + 1)
          : firstDaySlots.slice(0, this.firstTimeSlotIndex + 1)
      } else {
        return []
      }
    },

    getAvailabilityPerDates (todayDate, tomorrowDate) {
      const promises = [axios.get(this.availabilityEndpoint + todayDate)]

      if (tomorrowDate) {
        promises.push(axios.get(this.availabilityEndpoint + tomorrowDate))
      }

      axios.all(promises).then(responses => {
        const availabilities = responses.map(response => (response && response.data) ? response.data.data : [])
        this.matchAvailabilityTimesWithSlots(availabilities)
      }, () => {})
    },

    matchAvailabilityTimesWithSlots (availabilities) {
      let numberOfAvailableSlots = 0

      this.timeSlots.forEach((day, dayIndex) => {
        let currentSlot = []

        day.forEach((hour, hourIndex) => {
          if (day.length > hourIndex) {
            numberOfAvailableSlots = 0
            numberOfAvailableSlots = availabilities[dayIndex].filter(
              (slot) =>
                hour + this.timeSlotLength > moment(slot.appointmentTime).hour() &&
                hour <= moment(slot.appointmentTime).hour()
            ).length
            currentSlot.push([hour, numberOfAvailableSlots])
          }
        })

        this.$set(this.availabilityPerSlot, dayIndex, currentSlot)
      })
    }
  }
}
</script>
